import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { TEMPLATE_CREATION_STEPS } from 'components/common/constants';
import AccountDetailsStep from './components/AccountDetailsStep';
import BankDetailsStep from './components/BankDetailsStep';
import PaymentDetailsStep from './components/PaymentDetailsStep';
import RecipientAdditionalDetailsStep from './components/RecipientAdditionalDetailsStep';
import RecipientDetailsStep from './components/RecipientDetailsStep';
import ReviewAndSaveTemplateStep from './components/ReviewAndSaveTemplateStep';
import PopUpForApprovalSuccessScheme from 'components/common/PopUpScheme/PopUpForApprovalSuccessScheme';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import CustomFormSlider from 'uikit/CustomFormSlider/CustomFormStepper';
import { Container } from 'uikit/Container/Container';
import { PopUp } from 'uikit/PopUp/PopUp';
import './CreateTemplatePage.scss';
import { NewContainer } from '../../../../uikit/NewContainer/NewContainer';

const popUpInitialData = {
  isShowPopUp: false,
  title: '',
  description: '',
  confirmButtonLabel: '',
  onConfirm: () => {}
};

const CreateTemplatePage = ({ customerNumber, userAccounts, paymentStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const [headerText, setHeaderText] = useState(i18n.getMessage('container.newTemplate'));
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  const renderCurrentStep = () => {
    switch (paymentStore.currentTemplateStep) {
      case TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS:
        return <AccountDetailsStep customerNumber={customerNumber} userAccounts={userAccounts} />;
      case TEMPLATE_CREATION_STEPS.BANK_DETAILS:
        return <BankDetailsStep customerNumber={customerNumber} userAccounts={userAccounts} />;
      case TEMPLATE_CREATION_STEPS.RECIPIENT_DETAILS:
        return <RecipientDetailsStep customerNumber={customerNumber} />;
      case TEMPLATE_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS:
        return <RecipientAdditionalDetailsStep customerNumber={customerNumber} userAccounts={userAccounts} />;
      case TEMPLATE_CREATION_STEPS.PAYMENT_DETAILS:
        return <PaymentDetailsStep customerNumber={customerNumber} />;
      case TEMPLATE_CREATION_STEPS.REVIEW_AND_SAVE_TEMPLATE:
        return <ReviewAndSaveTemplateStep customerNumber={customerNumber} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS) {
      setHeaderText(i18n.getMessage('container.accountDetails'));
    } else if (paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.BANK_DETAILS) {
      setHeaderText(i18n.getMessage('container.bankDetails'));
    } else if (paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.RECIPIENT_DETAILS) {
      setHeaderText(i18n.getMessage('container.recipientDetails'));
    } else if (paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS) {
      setHeaderText(i18n.getMessage('container.recipientAdditionalDetails'));
    } else if (paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.PAYMENT_DETAILS) {
      setHeaderText(i18n.getMessage('container.paymentDetails'));
    } else if (
      paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.REVIEW_AND_SAVE_TEMPLATE &&
      !paymentStore.isTemplateUpdating
    ) {
      setHeaderText(i18n.getMessage('container.reviewAndSaveTemplate'));
    } else if (
      paymentStore.currentTemplateStep === TEMPLATE_CREATION_STEPS.REVIEW_AND_SAVE_TEMPLATE &&
      paymentStore.isTemplateUpdating
    ) {
      setHeaderText(i18n.getMessage('container.editTemplate'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStore.currentTemplateStep]);

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  useEffect(() => {
    if (paymentStore.isTemplateSuccessfullyCreated) {
      resetPopUpData();

      setPopUpData({
        isShowPopUp: true,
        title: i18n.getMessage('templates.popUp.successfulCreation.title'),
        description: i18n.getMessage('templates.popUp.successfulCreation.description'),
        confirmButtonLabel: i18n.getMessage('templates.popUp.successfulCreation.confirm'),
        onConfirm: async () => {
          paymentStore.setIsTemplateSuccessfullyCreated(false);
          paymentStore.resetTemplateCreationData();
          resetPopUpData();
          navigate(ROUTE_PATHS.TEMPLATES);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStore.isTemplateSuccessfullyCreated]);

  useEffect(() => {
    if (paymentStore.isTemplateSuccessfullyUpdated) {
      resetPopUpData();

      setPopUpData({
        isShowPopUp: true,
        title: i18n.getMessage('templates.popUp.successfulUpdate.title'),
        description: i18n.getMessage('templates.popUp.successfulUpdate.description'),
        confirmButtonLabel: i18n.getMessage('templates.popUp.successfulUpdate.confirm'),
        onConfirm: async () => {
          paymentStore.setIsTemplateSuccessfullyUpdated(false);
          paymentStore.resetTemplateCreationData();
          resetPopUpData();
          navigate(ROUTE_PATHS.TEMPLATES);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStore.isTemplateSuccessfullyUpdated]);

  const handleBackButtonClick = () => {
    if (paymentStore.getCurrentStepIndex() === 0) {
      navigate(ROUTE_PATHS.TEMPLATES);
      paymentStore.resetTemplateCreationData();
    } else {
      paymentStore.goToPrevStep();
    }
  };

  return (
    <NewContainer
      className={'content-card-wrapper'}
      header={headerText}
      isBackButton={true}
      handleBackButtonClick={handleBackButtonClick}
    >
      {paymentStore.currentTemplateStep !== TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS && (
        <CustomFormSlider
          className={'create-template-page-stepper'}
          steps={paymentStore.templateSteps}
          currentStep={paymentStore.getCurrentStepIndex()}
        />
      )}

      {renderCurrentStep()}

      <PopUp className={'prepared-transactions'} show={popUpData.isShowPopUp} onClose={resetPopUpData}>
        <PopUpForApprovalSuccessScheme
          title={popUpData.title}
          description={popUpData.description}
          confirmButtonLabel={popUpData.confirmButtonLabel}
          onConfirm={popUpData.onConfirm}
          onClose={popUpData.onConfirm}
        />
      </PopUp>
    </NewContainer>
  );
};

CreateTemplatePage.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore.userAccounts,
  paymentStore: stores.paymentStore
}))(observer(CreateTemplatePage));
